/**
 * Formular „Neue Abteilung": Name, Kürzel, Farbe, Beschreibung, Personengruppe
 * (zwei Radio-Karten, nach dem Anlegen fest) und Rollen der Abteilung
 * (vorhandene Rollen aus Rollen & Rechte, kein Anlegen).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Field, Input, MultiSelect, SectionTitle } from '../../../components/ui';
import { departmentsApi, type Department, type DepartmentUserRole, type RbacRole } from '../../../lib/departments';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { apiErrorMessage, SLUG_RE, slugify } from './shared';

interface Props {
  dark: boolean;
  roles: RbacRole[];
  onCancel: () => void;
  onCreated: (d: Department) => void | Promise<void>;
}

export function AbteilungNewForm({ dark, roles, onCancel, onCreated }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [color, setColor] = useState('#2e90fa');
  const [description, setDescription] = useState('');
  const [userRole, setUserRole] = useState<DepartmentUserRole | null>(null);
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const slugValid = SLUG_RE.test(slug);
  const valid = name.trim().length > 0 && slugValid && userRole != null;

  const submit = async () => {
    setSubmitted(true);
    if (!valid || userRole == null) return;
    setSaving(true);
    setError(null);
    try {
      const created = await departmentsApi.create({
        name: name.trim(),
        slug,
        color,
        description: description.trim() || undefined,
        user_role: userRole,
        ...(roleIds.length > 0 ? { role_ids: roleIds } : {}),
      });
      await onCreated(created);
    } catch (e) {
      setError(apiErrorMessage(e, tr('departments.new.save_error')));
    } finally {
      setSaving(false);
    }
  };

  const groupCard = (value: DepartmentUserRole, key: 'pb' | 'bk') => {
    const active = userRole === value;
    return (
      <label
        key={key}
        style={{
          display: 'block', cursor: 'pointer', borderRadius: 12, padding: '12px 14px',
          border: `1.5px solid ${active ? BRAND : t.border}`, background: active ? BRAND_LIGHT : t.card,
        }}
      >
        <input
          type="radio"
          name="department-user-role"
          value={value}
          checked={active}
          onChange={() => setUserRole(value)}
          style={{ accentColor: BRAND, marginRight: 8 }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{tr(`departments.group.${key}_card`)}</span>
        <div style={{ fontSize: 12, color: t.muted, margin: '4px 0 0 22px' }}>{tr(`departments.group.${key}_hint`)}</div>
      </label>
    );
  };

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle dark={dark} title={tr('departments.new.title')} subtitle={tr('departments.new.subtitle')} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label={tr('departments.fields.name')} error={submitted && !name.trim() ? tr('departments.fields.required') : undefined}>
          <Input
            dark={dark}
            aria-label={tr('departments.fields.name')}
            value={name}
            onChange={e => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
          />
        </Field>
        <Field
          label={tr('departments.fields.slug')}
          hint={tr('departments.fields.slug_hint')}
          error={submitted && !slugValid ? tr('departments.fields.slug_invalid') : undefined}
        >
          <Input dark={dark} aria-label={tr('departments.fields.slug')} value={slug} onChange={e => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }} />
        </Field>
        <Field label={tr('departments.fields.color')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input
              type="color"
              aria-label={tr('departments.fields.color')}
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 40, height: 34, border: 'none', background: 'transparent', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, color: t.muted }}>{color}</span>
          </div>
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field label={tr('departments.fields.description')}>
          <Input dark={dark} aria-label={tr('departments.fields.description')} value={description} onChange={e => setDescription(e.target.value)} />
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field label={tr('departments.fields.roles')} hint={tr('departments.fields.roles_hint')}>
          <MultiSelect
            dark={dark}
            options={roles.map(r => ({ id: r.id, label: r.name }))}
            value={roleIds}
            onChange={setRoleIds}
            placeholder={tr('departments.fields.roles_placeholder')}
            ariaLabel={tr('departments.fields.roles')}
            removeLabel={n => tr('departments.common.remove_named', { name: n })}
          />
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <Field
          label={tr('departments.fields.group')}
          error={submitted && userRole == null ? tr('departments.fields.required') : undefined}
        >
          <div role="radiogroup" aria-label={tr('departments.fields.group')} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {groupCard(2, 'pb')}
            {groupCard(1, 'bk')}
          </div>
        </Field>
        <div style={{ fontSize: 12, color: t.muted, marginTop: 6 }}>{tr('departments.fields.group_fixed')}</div>
      </div>
      {error && <div role="alert" style={{ color: '#b42318', fontSize: 13, marginTop: 14 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <Button dark={dark} variant="primary" disabled={saving} onClick={() => void submit()}>{tr('departments.new.submit')}</Button>
        <Button dark={dark} variant="secondary" onClick={onCancel}>{tr('departments.common.cancel')}</Button>
      </div>
    </Card>
  );
}
